import { LoadingOutlined } from '@ant-design/icons'
import bwipjs from 'bwip-js'
import React from 'react'
import { productToOldBarcode } from '@wolf/barcodes'
import { format, secondsToMinSec } from '@wolf/dates'
import { getTranslations } from './TranslationTemplate'
import { AllergensBlock } from './shared/AllergensBlock'
import { IngredientsBlock } from './shared/IngredientsBlock'
import { NutritionTable } from './shared/NutritionTable'
import { PdfFrame } from '../../../../components/labels/PdfFrame'
import { usePageOutletContext } from '../../../../page-outlet-context'
import { trpc } from '../../../../utils/trpc'

type Props = {
  productId: number
  expirationDate: Date
  quantity: number
  onClose: () => void
}

export const SingleLabelTemplate: React.FC<Props> = ({
  productId,
  expirationDate,
  quantity,
  onClose,
}) => {

  const { sourceFilter, notificationInstance } = usePageOutletContext()

  const t = getTranslations(sourceFilter.language)

  const showError = (errorMessage: string) =>
    notificationInstance.error({
      message: 'Error en formulario',
      description: errorMessage,
    })

  const productQuery = trpc.products.getProductById.useQuery({
    productId: productId,
    sourceId: sourceFilter.serialId,
  }, {
    retry: false,
    onError: (error) => {
      showError(error.message)
    },
  })

  const contentArray = new Array(quantity).fill(0)

  if (productQuery.isLoading) {
    return <LoadingOutlined />
  }

  if (!productQuery.data) {
    return <></>
  }

  const { data: product } = productQuery

  const barcodes = contentArray.map((_, index) => {
    /* TODO: Uncomment once we migrated fully from trastaca
    const productCode = {
      sourceId: source.serialId,
      workspaceId: workspace.serialId,
      salesGroupId,
      productId: product.productId,
      date: now,
      index: index + 1,
    }

    const dataMatrixCodeText = productV2ToCode(productCode)
    */

    const productOldCode = {
      productId: product.productId,
      date: new Date(),
      index: index + 1,
    }

    const barcodeText = productToOldBarcode(productOldCode)

    return {
      /* TODO: Uncomment once we migrated fully from trastaca
      datamatrix: {
        imageDataURL: bwipjs
          .toCanvas(document.createElement('canvas'), {
            bcid: 'datamatrix',
            text: dataMatrixCodeText,
          })
          .toDataURL('image/png'),
        codeText: dataMatrixCodeText
      },
      */
      barcode: {
        imageDataURL: bwipjs
          .toCanvas(document.createElement('canvas'), {
            bcid: 'code128',
            text: barcodeText,
          })
          .toDataURL('image/png'),
        codeText: `L-${barcodeText}`
      }
    }
  })

  return (
    <PdfFrame onClose={onClose} waitingTime={10000}>
      <link
        type="text/css"
        rel="Stylesheet"
        href="/styles/SingleLabelTemplate.css"
      />
      {contentArray.map((_, index) => {
        return (
          <div className="content" key={index}>
            <div className="column">
              <div className="column1">
                <div className="micro">
                  {product.tuppers.map((tupper) => {
                    return (
                      <div className="micro1" key={tupper.name}>
                        <img
                          className="micro-img"
                          src={`/images/${tupper.cooking !== 0 ? 'heat' : 'noheat'}.svg`}
                        />
                        <div className="micro-text">
                          <p className="micro-time">
                            {tupper.cooking !== 0
                              ? secondsToMinSec(tupper.cooking)
                              : t.notHeat}
                          </p>
                          {product.tuppers.length > 1 && <p className="micro-name">{tupper.label ?? tupper.name}</p>}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
              <div className="column2 topcolumn">
                <p className="logo">
                  <img className="logo" src="/images/pan.svg" />
                </p>
              </div>
            </div>

            <div className="column">
              <div className="column1">
                <IngredientsBlock productName={product.name} ingredients={product.ingredients} t={t} />

                <NutritionTable weight={product.weight} nutritionFacts={product.nutritionFacts} t={t} />
              </div>

              <div className="column2 column2-left">
                <div>
                  <p className="date-text">{t.expirationDate}:</p>
                  <p className="date">
                    {format(expirationDate, 'dd/MM/yyyy')}
                  </p>

                  <p className="date-information">
                    {t.protectAtmosphere}
                  </p>
                </div>
                <AllergensBlock allergens={product.allergens} t={t} />
                <div className="code">
                  {/* TODO: Uncomment once we migrated fully from trastaca
                  <div className="code-icon">
                    <img width={80} src={barcodes[index].datamatrix.imageDataURL} />
                    <p className="code-icon-text">{barcodes[index].datamatrix.codeText}</p>
                  </div>
                  */}

                  <div className="code-icon">
                    <img width={120} src={barcodes[index].barcode.imageDataURL} />
                    <p className="code-icon-text">{barcodes[index].barcode.codeText}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="name-dish">
              <div className="name-dish-title">
                <strong>{product.name}</strong>
              </div>
            </div>
          </div>
        )
      })}
    </PdfFrame>
  )
}
